<template>
  <ModalDialog
    ref="dialog"
    title="Add Payment"
    :submitting="submitting"
    :submitDisabled="submitting || !canSubmitPayment"
    submitTitle="Add Payment"
    icon="fa-credit-card"
    :scrollable="true"
    sizeClass="modal-xl"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm">
      <FormRow label="Contact">
        <ContactSearchableSelect :isCustomer="true" :activeOnly="true" v-model="contactId" field="contactId" />
      </FormRow>
      <div class="add-payment-container">

        <p v-if="!contactId" class="text-center text-muted font-italic py-4">Select a contact to add a payment</p>

        <div v-if="!loading && !loadingError && contactId">
          <FormRow label="Type">
            <FormSelect :options="PaymentTypeLookup" v-model="payment.paymentType" field="paymentType" />
          </FormRow>
          <FormRow label="Location">
            <AccountLocationSelect v-model="payment.accountLocationId" field="accountLocationId" />
          </FormRow>
          <FormRow label="Reference" :fieldCount="2">
            <template v-slot:field1>
              <FormInputText v-model="payment.reference" placeholder="Payment Reference" field="reference" />
            </template>
            <template v-slot:field2>
              <FormInputText v-model="payment.description" placeholder="Additional description" field="description" />
            </template>
          </FormRow>
          <FormRow label="Amount" :fieldCount="2">
            <template v-slot:field1>
              <FormInputCurrency ref="paymentAmount" v-model="payment.amount" :addonSlot="true" field="amount">
                <template v-slot:addon>
                            
                  <button data-toggle="dropdown" class="btn btn-white dropdown-toggle" type="button"></button>
                  <ul class="dropdown-menu">
                    <DropdownMenuItem icon="fa-clock" @click="payAllOverdueInvoices">Pay all overdue invoices</DropdownMenuItem>
                    <DropdownMenuItem icon="fa-envelope-open-dollar" @click="payAllInvoices">Pay all invoices</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem icon="fa-shopping-cart" @click="payAllOrders">Pay all orders</DropdownMenuItem>
                  </ul>
                </template> 
              </FormInputCurrency>
            </template>
            <template v-slot:field2>
              <FormDate v-model="payment.date" placeholder="Payment Date" field="date" />
            </template>
          </FormRow>

          <FormHorizontalRule class="my-2" />
          <TabbedBox>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-allocations-invoice">
                <i class="far fa-envelope-open-dollar"></i> Invoice Allocations
              </Tab>
              <Tab tabid="tab-allocations-orders">
                <i class="far fa-shopping-cart"></i> Order Allocations
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-allocations-invoice">
                <InvoiceAllocation
                  :contactId="contactId"
                  :defaultInvoiceId="invoiceId"
                  :isFixedAmount="false"
                  :isPaymentMode="true"
                  :paymentAmount="currentPayment"
                  v-model="availableInvoiceList"
                  @updatePayment="payment.amount = $event" />
              </TabPage>
              <TabPage tabid="tab-allocations-orders">
                <OrderAllocationList
                  :contactId="contactId"
                  :defaultOrderId="orderId"
                  :paymentAmount="currentPayment"
                  v-model="availableOrderList"
                  @updatePayment="payment.amount = $event"
                />
              </TabPage>
            </template>
          </TabbedBox>

          <FormHorizontalRule class="my-2" />
          <PaymentAllocationSummary 
            :invoiceAllocationList="availableInvoiceList"
            :orderAllocationList="availableOrderList"
            :isPaymentMode="true"
            :isFixedAmount="false"
            :paymentAmount="currentPayment"
          />
        
        </div>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<style scoped>
.add-payment-container {
  min-height: 300px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import ContactSearchableSelect from '@/modules/contacts/components/contactSearchableSelect'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import InvoiceAllocation from '@/modules/accounts/components/invoiceAllocation'
import OrderAllocationList from '@/modules/accounts/components/orderAllocationList'
import PaymentAllocationSummary from '@/modules/accounts/components/paymentAllocationSummary'

import { PaymentTypes, PaymentTypeLookup, InvoiceStatusTypes } from '@/store/types'

export default {
  name: 'AddPaymentDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    ContactSearchableSelect,
    AccountLocationSelect,
    InvoiceAllocation,
    OrderAllocationList,
    PaymentAllocationSummary
  },
  props: {
  },
  data() {
    return {
      dialogForm: {},
      payment: { },
      availableInvoiceList: [],
      availableOrderList: [],
      invoiceId: null,
      orderId: null,
      contactId: null,
      contactData: {},
      loading: false,
      loadingError: '',
      submitting: false,
    }
  },
  created() {
    this.PaymentTypes = PaymentTypes
    this.PaymentTypeLookup = PaymentTypeLookup    
  },
  watch: {
    contactId: function() {
      this.requestContactData()
    }
  },
  mounted() {
  },
  computed: {
    canSubmitPayment() {
      if(!this.contactId)
        return false
      if(this.currentPayment < Utils.roundCurrency(this.totalInvoiceAllocation + this.totalOrderAllocation))
        return false
      if(this.totalInvoiceErrorCount > 0 || this.totalOrderErrorCount > 0)
        return false
      if(this.currentPayment == 0)
        return false

      return true
    },
    currentPayment() {
      return this.parseDecimal(this.payment.amount)
    },
    totalInvoiceAllocation() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(invoice.allocatedAmount))
      }, 0)
    },
    totalOverpaymentValue() {
      let overAmount = Utils.roundCurrency(this.currentPayment - this.totalInvoiceAllocation - this.totalOrderAllocation)
      return Math.max(overAmount, 0)
    },
    totalInvoiceCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).length
    },
    totalOrderCount() {
      return this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).length
    },
    totalInvoiceErrorCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > invoice.amountDue).length
    },
    totalOrderErrorCount() {
      return this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > order.amountDue).length
    },
    totalOrderAllocation() {
      return this.availableOrderList.reduce((curVal, order) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(order.allocatedAmount))
      }, 0)
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.contactId = options ? options.contactId : null
      this.invoiceId = options ? options.invoiceId : null    // If paying a specific invoice
      this.orderId = options ? options.orderId : null    // If paying a specific order

      // Defaults
      this.payment = {
        paymentType: PaymentTypes.creditCard,
        amount: 0,
        date: moment().local().toISOString(),
        reference: '',
        description: '',
        accountLocationId: null
      }

      // Default to the user's default account location
      if(this.$store.state.profile.profileUser.accountLocationId) {
        this.payment.accountLocationId = this.$store.state.profile.profileUser.accountLocationId
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let invoiceCount = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).length
      let paymentDisplay = Utils.formatCurrency(this.payment.amount)
      let overPaymentDisplay = Utils.formatCurrency(this.totalOverpaymentValue)
      let contactName = this.contactData.name
      
      var message = `Confirm payment of <strong>${paymentDisplay}</strong> received by <strong>${contactName}</strong>?`
      if(this.totalOverpaymentValue > 0)
        message += `<br /><br /><strong>Note</strong>: An overpayment of <strong>${overPaymentDisplay}</strong> will be added!`
      this.confirmMessage('Confirm Payment?', null, { html: message })
      .then(success => {
        if(success) {
          this._submitPayment()
        }
      })
    },
    _submitPayment() {
      var createPaymentData = {
        contactId: this.contactId,
        accountLocationId: this.payment.accountLocationId,
        paymentType: this.payment.paymentType,
        amount: Utils.roundCurrency(parseFloat(this.payment.amount)),
        overpaymentAmount: Utils.roundCurrency(this.totalOverpaymentValue),
        date: this.payment.date,
        reference: this.payment.reference,
        description: this.payment.description
      }

      createPaymentData.invoiceAllocationList = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).map(invoice => {
        return {
          invoiceId: invoice.id,
          amount: Utils.roundCurrency(this.parseDecimal(invoice.allocatedAmount))
        }
      })
      createPaymentData.orderAllocationList = this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).map(order => {
        return {
          orderId: order.id,
          amount: Utils.roundCurrency(this.parseDecimal(order.allocatedAmount))
        }
      })

      this.submitting = true
      this.$apiService.accountsService.createPaymentReceived(createPaymentData)
      .then(response => {
        this.modalClosed(response.data.id)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    requestContactData() {
      this.contactData = {}
      this.loadingError = ''

      this.$apiService.contactService.getContact(this.contactId)
      .then(response => {
        this.contactData = response.data

        // Set the default payment type if the contact has one
        if(this.contactData.defaultPaymentType) {
          this.payment.paymentType = this.contactData.defaultPaymentType
        }
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
      })
    },

    payAllOverdueInvoices() {
      this.availableInvoiceList.forEach((invoice, index) => {
        if(invoice.isOverDue) {
          invoice.allocatedAmount = Utils.roundCurrency(invoice.amountDue)
        } else {
          invoice.allocatedAmount = 0
        }

        this.$set(this.availableInvoiceList, index, invoice)
      })

      this.payment.amount = Utils.roundCurrency(this.totalInvoiceAllocation)
    },
    payAllInvoices() {
      this.availableInvoiceList.forEach((invoice, index) => {
        invoice.allocatedAmount = Utils.roundCurrency(invoice.amountDue)

        this.$set(this.availableInvoiceList, index, invoice)
      })

      this.payment.amount = Utils.roundCurrency(this.totalInvoiceAllocation)
    },
    payAllOrders() {
      this.availableOrderList.forEach((order, index) => {
        order.allocatedAmount = Utils.roundCurrency(order.amountDue)

        this.$set(this.availableOrderList, index, order)
      })

      this.payment.amount = Utils.roundCurrency(this.totalOrderAllocation)
    }
  }
}
</script>